import type { Screen0LanguageCode } from "@/features/language/language-registry";
import { resolveAppCategory } from "../apps/categoryNormalization";
import { buildExcludedRawKeySet, insightRawAppKey, isRawAppKeyExcluded } from "../apps/appFilterKeys";
import { isUnattributedApp } from "../native/mergeSameDaySnapshot";
import type { Rule, Target } from "../rules/ruleTypes";
import {
  buildSmartBlockSuggestions,
  coarseBandWindow,
  type BlockWindow,
  type SmartBlockCandidate,
  type SmartBlockCopy,
  type SmartBlockGoal,
} from "./smartBlockEngine";
import { resolveSmartBlockSuggestionsAgainstRules } from "./smartBlockRuleMatching";

export type InsightWindowApp = {
  appIdentifier?: string;
  displayName: string;
  category?: string;
  durationSeconds: number;
  opens?: number;
  color?: string;
  /** Sparse per-app hourly buckets (local hour 0..23). Absent hours are zero. */
  hourly?: Array<{ hour: number; seconds: number; opens?: number }>;
};

export type InsightWindowDay = {
  date: string;
  totalScreenTimeSeconds: number;
  permissionStatus: "granted" | "denied" | "unavailable";
  collectionStatus: "success" | "missing_permission" | "partial" | "native_error" | "metric_unsupported" | "no_data";
  reason?: string;
  topApps: InsightWindowApp[];
  timeBands: Array<{ key: string; seconds: number }>;
};

export type InsightWindowResult = {
  window: { startDate: string; endDate: string; daysRequested: number };
  permissionStatus: "granted" | "denied" | "unavailable";
  days: InsightWindowDay[];
};

export type InsightMetric = {
  appKey: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
  color?: string;
  score: number;
  totalSeconds: number;
  opens: number;
  activeDays: number;
  daysWithData: number;
  timeShare: number;
  recurrence: number;
  /** Temporal concentration 0..100 (doom-scroll signal). */
  concentration: number;
  components: {
    opensProxy: number;
    timeShare: number;
    recurrence: number;
    concentration: number;
  };
};

export type SmartBlockSuggestion = {
  id: string;
  appKey: string;
  /** Raw label from usage; resolve with friendlyAppName for display. */
  displayName: string;
  appIdentifier?: string;
  category?: string;
  goal: SmartBlockGoal;
  goalLabel: string;
  /** Block window, same calendar day. startHour inclusive, endHour exclusive. */
  startHour: number;
  endHour: number;
  /** e.g. "Block completely 14:00–17:00". */
  actionLabel: string;
  reason: string;
  /** Present after resolving against live rules. */
  mode?: "activate" | "reactivate";
  existingRuleId?: string;
  reactivateHint?: string;
};

export type InsightScores = {
  status: "ready" | "missing_permission" | "no_data" | "insufficient_history";
  windowLabel: string;
  daysWithData: number;
  totalSeconds: number;
  hasOpenSignal: boolean;
  compulsivity: InsightMetric | null;
  doomScrollRisk: InsightMetric | null;
  /** Top-ranked compulsivity apps, #1 first, capped at 3. */
  compulsivityTop: InsightMetric[];
  /** Top-ranked doom-scroll risk apps, #1 first, capped at 3. */
  doomScrollRiskTop: InsightMetric[];
  /** Full compulsivity ranking for Smart Block active-rule ordering. */
  compulsivityRanked: InsightMetric[];
  /** Full doom-scroll ranking for Smart Block active-rule ordering. */
  doomScrollRiskRanked: InsightMetric[];
  suggestions: SmartBlockSuggestion[];
};

type AppAggregate = {
  appKey: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
  color?: string;
  totalSeconds: number;
  opens: number;
  activeDates: Set<string>;
  /** Per-hour seconds across the window, length 24. */
  hourlySeconds: number[];
  /** Per-hour opens across the window, length 24. */
  hourlyOpens: number[];
};

const MIN_APP_SECONDS = 60;

function clampScore(value: number) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function componentFromShare(share: number, cap: number) {
  if (cap <= 0) return 0;
  return clampScore((share / cap) * 100);
}

/**
 * Temporal concentration 0..100 from per-app hourly seconds, derived from
 * normalized entropy: usage packed into a few hours scores high regardless of
 * which hours (no time-of-day bias). Zero when hourly data is unavailable.
 */
function concentrationScore(hourlySeconds: number[]) {
  const total = hourlySeconds.reduce((sum, seconds) => sum + Math.max(0, seconds), 0);
  if (total <= 0) return 0;
  let entropy = 0;
  let activeHours = 0;
  for (const seconds of hourlySeconds) {
    if (seconds <= 0) continue;
    activeHours += 1;
    const share = seconds / total;
    entropy -= share * Math.log(share);
  }
  if (activeHours <= 1) return 100;
  const maxEntropy = Math.log(24);
  return clampScore((1 - entropy / maxEntropy) * 100);
}

function metricFromAggregate(
  aggregate: AppAggregate,
  context: {
    totalSeconds: number;
    maxOpens: number;
    daysWithData: number;
    hasOpenSignal: boolean;
    kind: "compulsivity" | "doom";
  },
): InsightMetric {
  const timeShare = context.totalSeconds > 0 ? aggregate.totalSeconds / context.totalSeconds : 0;
  const recurrence = context.daysWithData > 0 ? aggregate.activeDates.size / context.daysWithData : 0;

  // Proxy scores intentionally normalize aggregate signals; the app does not
  // have notification attribution or uninterrupted session telemetry yet.
  const opensProxyScore = context.hasOpenSignal && context.maxOpens > 0
    ? clampScore((aggregate.opens / context.maxOpens) * 100)
    : 0;
  const timeShareScore = componentFromShare(timeShare, 0.4);
  const recurrenceScore = clampScore(recurrence * 100);
  const concentration = concentrationScore(aggregate.hourlySeconds);
  const score = context.kind === "compulsivity"
    ? clampScore(opensProxyScore * 0.4 + timeShareScore * 0.35 + recurrenceScore * 0.25)
    : clampScore(timeShareScore * 0.4 + recurrenceScore * 0.35 + concentration * 0.25);

  return {
    appKey: aggregate.appKey,
    displayName: aggregate.displayName,
    appIdentifier: aggregate.appIdentifier,
    category: aggregate.category,
    color: aggregate.color,
    score,
    totalSeconds: aggregate.totalSeconds,
    opens: aggregate.opens,
    activeDays: aggregate.activeDates.size,
    daysWithData: context.daysWithData,
    timeShare,
    recurrence,
    concentration,
    components: {
      opensProxy: opensProxyScore,
      timeShare: timeShareScore,
      recurrence: recurrenceScore,
      concentration,
    },
  };
}

function rankMetrics(metrics: InsightMetric[]) {
  return [...metrics].sort((a, b) =>
    b.score - a.score ||
    b.totalSeconds - a.totalSeconds ||
    b.opens - a.opens ||
    a.displayName.localeCompare(b.displayName),
  );
}

const BAND_KEYS = ["morning", "afternoon", "evening", "night"] as const;

export function scoreInsightWindow(
  result: InsightWindowResult,
  options?: {
    rules?: Rule[];
    targets?: Target[];
    smartBlockCopy?: SmartBlockCopy;
    languageCode?: Screen0LanguageCode;
    /** Namespaced `app:…` keys excluded from Insight metrics and suggestions. */
    excludedAppKeys?: string[];
  },
): InsightScores {
  const excludedRawKeys = buildExcludedRawKeySet(options?.excludedAppKeys);
  const usableDays = result.days
    .filter((day) =>
      day.permissionStatus === "granted" &&
      day.collectionStatus !== "missing_permission" &&
      day.collectionStatus !== "metric_unsupported" &&
      day.topApps.some((app) => app.durationSeconds > 0),
    )
    .sort((a, b) => a.date.localeCompare(b.date));

  if (result.permissionStatus !== "granted" && usableDays.length === 0) {
    return emptyScores("missing_permission", result.window.daysRequested);
  }
  if (usableDays.length === 0) return emptyScores("no_data", result.window.daysRequested);

  const aggregates = new Map<string, AppAggregate>();
  const bandSeconds: Record<string, number> = { morning: 0, afternoon: 0, evening: 0, night: 0 };
  let totalSeconds = 0;
  let hasOpenSignal = false;

  for (const day of usableDays) {
    const dayTopAppSeconds = day.topApps.reduce((sum, app) => sum + Math.max(0, app.durationSeconds), 0);
    const dayTotal = day.totalScreenTimeSeconds > 0 ? day.totalScreenTimeSeconds : dayTopAppSeconds;
    totalSeconds += dayTotal;
    for (const key of BAND_KEYS) {
      bandSeconds[key] += day.timeBands.find((band) => band.key === key)?.seconds ?? 0;
    }

    for (const app of day.topApps) {
      if (isUnattributedApp(app)) continue;
      const key = insightRawAppKey(app);
      if (!key || app.durationSeconds <= 0) continue;
      if (isRawAppKeyExcluded(key, excludedRawKeys)) continue;
      if ((app.opens ?? 0) > 0) hasOpenSignal = true;
      const aggregate = aggregates.get(key) ?? {
        appKey: key,
        displayName: app.displayName,
        appIdentifier: app.appIdentifier,
        category: app.category,
        color: app.color,
        totalSeconds: 0,
        opens: 0,
        activeDates: new Set<string>(),
        hourlySeconds: new Array<number>(24).fill(0),
        hourlyOpens: new Array<number>(24).fill(0),
      };
      aggregate.totalSeconds += app.durationSeconds;
      aggregate.opens += app.opens ?? 0;
      aggregate.category = resolveAppCategory(
        aggregate.category,
        app.category,
        aggregate.appIdentifier ?? app.appIdentifier,
      );
      aggregate.activeDates.add(day.date);
      for (const bucket of app.hourly ?? []) {
        if (bucket.hour < 0 || bucket.hour > 23) continue;
        aggregate.hourlySeconds[bucket.hour] += Math.max(0, bucket.seconds);
        if (bucket.opens != null) aggregate.hourlyOpens[bucket.hour] += Math.max(0, bucket.opens);
      }
      aggregates.set(key, aggregate);
    }
  }

  const candidates = [...aggregates.values()].filter((aggregate) => aggregate.totalSeconds >= MIN_APP_SECONDS);
  if (candidates.length === 0) {
    if (aggregates.size === 0) {
      return {
        status: usableDays.length < 3 ? "insufficient_history" : "ready",
        windowLabel: `${result.window.daysRequested} days`,
        daysWithData: usableDays.length,
        totalSeconds,
        hasOpenSignal,
        compulsivity: null,
        doomScrollRisk: null,
        compulsivityTop: [],
        doomScrollRiskTop: [],
        compulsivityRanked: [],
        doomScrollRiskRanked: [],
        suggestions: [],
      };
    }
    return emptyScores("no_data", result.window.daysRequested, usableDays.length, totalSeconds, hasOpenSignal);
  }

  const scoringTotalSeconds = candidates.reduce((sum, aggregate) => sum + aggregate.totalSeconds, 0);
  const maxOpens = Math.max(...candidates.map((aggregate) => aggregate.opens), 0);
  const compulsivity = rankMetrics(candidates.map((aggregate) =>
    metricFromAggregate(aggregate, {
      totalSeconds: scoringTotalSeconds,
      maxOpens,
      daysWithData: usableDays.length,
      hasOpenSignal,
      kind: "compulsivity",
    }),
  ));
  const doom = rankMetrics(candidates.map((aggregate) =>
    metricFromAggregate(aggregate, {
      totalSeconds: scoringTotalSeconds,
      maxOpens,
      daysWithData: usableDays.length,
      hasOpenSignal,
      kind: "doom",
    }),
  ));

  const compulsivityByKey = new Map(compulsivity.map((metric) => [metric.appKey, metric.score]));
  const doomByKey = new Map(doom.map((metric) => [metric.appKey, metric.score]));
  const smartCandidates: SmartBlockCandidate[] = candidates.map((aggregate) => ({
    appKey: aggregate.appKey,
    displayName: aggregate.displayName,
    appIdentifier: aggregate.appIdentifier,
    category: aggregate.category,
    color: aggregate.color,
    compulsivityScore: compulsivityByKey.get(aggregate.appKey) ?? 0,
    doomScore: doomByKey.get(aggregate.appKey) ?? 0,
    totalSeconds: aggregate.totalSeconds,
    opens: aggregate.opens,
    hourlySeconds: aggregate.hourlySeconds,
    hourlyOpens: aggregate.hourlyOpens,
  }));

  const fallbackWindow = coarseBandWindow(bandSeconds);
  const smartBlockCopy = options?.smartBlockCopy;
  const languageCode = options?.languageCode ?? "en";
  const suggestions = options?.rules && options?.targets && smartBlockCopy
    ? resolveSmartBlockSuggestionsAgainstRules(
        smartCandidates,
        fallbackWindow,
        options.rules,
        options.targets,
        smartBlockCopy,
        languageCode,
      )
    : smartBlockCopy
      ? buildSmartBlockSuggestions(smartCandidates, fallbackWindow, smartBlockCopy, undefined, undefined, languageCode)
      : [];

  return {
    status: usableDays.length < 3 ? "insufficient_history" : "ready",
    windowLabel: `${result.window.daysRequested} days`,
    daysWithData: usableDays.length,
    totalSeconds,
    hasOpenSignal,
    compulsivity: compulsivity[0] ?? null,
    doomScrollRisk: doom[0] ?? null,
    compulsivityTop: compulsivity.slice(0, 3),
    doomScrollRiskTop: doom.slice(0, 3),
    compulsivityRanked: compulsivity,
    doomScrollRiskRanked: doom,
    suggestions,
  };
}

function emptyScores(
  status: InsightScores["status"],
  daysRequested: number,
  daysWithData = 0,
  totalSeconds = 0,
  hasOpenSignal = false,
): InsightScores {
  return {
    status,
    windowLabel: `${daysRequested} days`,
    daysWithData,
    totalSeconds,
    hasOpenSignal,
    compulsivity: null,
    doomScrollRisk: null,
    compulsivityTop: [],
    doomScrollRiskTop: [],
    compulsivityRanked: [],
    doomScrollRiskRanked: [],
    suggestions: [],
  };
}
